import React, { useState, useEffect } from 'react';
import { Spinner, Text, makeStyles, shorthands } from '@fluentui/react-components';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';
import { useAuth } from '../hooks/useAuth';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useSwaggerSpec } from '../hooks/useSwaggerSpec';
import { SWAGGER_UI_FIX_STYLES } from './swaggerPageStyles';
import SwaggerSpecTabs from './SwaggerSpecTabs';
import { SPEC_MODE, getInitialSpecMode, syncSpecModeInUrl } from './swaggerPageConfig';

const useStyles = makeStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: '#ffffff',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('8px', '16px', '4px'),
    flexShrink: 0,
  },
  tabs: {
    minHeight: 'unset',
  },
  loadingContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.gap('16px'),
    opacity: 1,
    ...shorthands.padding('16px'),
  },
  swaggerContainer: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
    ...shorthands.padding('0', '0', '12px'),
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: '300ms',
    animationTimingFunction: 'ease-in',
  },
});

function SwaggerPage() {
  const classes = useStyles();
  const { getAccessToken } = useAuth();
  const isAdmin = useIsAdmin();
  const [specMode, setSpecMode] = useState(getInitialSpecMode);
  const { isLoading, swaggerAuthToken, swaggerSpec } = useSwaggerSpec(specMode, getAccessToken);

  useEffect(() => {
    if (!isAdmin && specMode === SPEC_MODE.INTERNAL) {
      setSpecMode(SPEC_MODE.PUBLIC);
    }
  }, [isAdmin, specMode]);

  const normalizeTopControls = () => {
    const swaggerRoot = document.querySelector('.swagger-page-scope .swagger-ui');
    if (!swaggerRoot) {
      return;
    }

    const schemeContainer = swaggerRoot.querySelector('.scheme-container');
    if (!schemeContainer) {
      return;
    }

    const selectorLabels = Array.from(schemeContainer.querySelectorAll('.schemes label'))
      .filter((label) => label.querySelector('select'));

    const infoCard = swaggerRoot.querySelector('.information-container .info');
    if (infoCard) {
      const titleGroup = infoCard.querySelector('hgroup.main');

      let headerRow = infoCard.querySelector('.swagger-info-header');
      if (!headerRow) {
        headerRow = document.createElement('div');
        headerRow.className = 'swagger-info-header';

        if (titleGroup && titleGroup.parentElement === infoCard) {
          infoCard.insertBefore(headerRow, titleGroup);
          headerRow.appendChild(titleGroup);
        } else {
          infoCard.insertBefore(headerRow, infoCard.firstChild);
        }
      }

      let titleControls = headerRow.querySelector('.swagger-title-controls');
      if (!titleControls) {
        titleControls = document.createElement('div');
        titleControls.className = 'swagger-title-controls';
        headerRow.appendChild(titleControls);
      }

      if (schemeContainer.parentElement !== titleControls) {
        titleControls.appendChild(schemeContainer);
      }
    }

    selectorLabels.forEach((label) => {
      label.style.display = '';

      label.querySelectorAll('.schemes-title').forEach((titleNode) => {
        titleNode.style.display = 'none';
      });

      Array.from(label.childNodes)
        .filter((node) => node.nodeType === Node.TEXT_NODE)
        .forEach((textNode) => {
          textNode.textContent = '';
        });
    });

    schemeContainer.querySelectorAll('.servers-title').forEach((titleNode) => {
      titleNode.style.display = 'none';
    });

    const visibleSelectorCount = selectorLabels.length;
    const authorizeButton = schemeContainer.querySelector('.auth-wrapper .authorize, .btn.authorize');

    schemeContainer.style.display = visibleSelectorCount === 0 && !authorizeButton ? 'none' : '';

    const schemesRow = schemeContainer.querySelector('.schemes');
    if (schemesRow) {
      schemesRow.style.justifyContent = visibleSelectorCount === 0 ? 'flex-end' : '';
    }
  };

  useEffect(() => {
    syncSpecModeInUrl(specMode);
  }, [specMode]);

  const content = isLoading || !swaggerSpec
    ? (
        <div className={classes.loadingContainer}>
          <Spinner size="extra-large" label="Loading API Documentation..." />
          <Text>Please wait while we load the API specifications...</Text>
        </div>
      )
    : (
        <div className={classes.swaggerContainer}>
          <SwaggerUI
            spec={swaggerSpec}
            onComplete={(swaggerUi) => {
              if (swaggerAuthToken) {
                swaggerUi.preauthorizeApiKey('bearerAuth', swaggerAuthToken);
              }

              normalizeTopControls();
            }}
            requestInterceptor={(req) => {
              if (swaggerAuthToken) {
                req.headers['Authorization'] = `Bearer ${swaggerAuthToken}`;
              }
              return req;
            }}
            docExpansion="none"
            defaultModelsExpandDepth={1}
            displayRequestDuration={true}
            filter={true}
            showExtensions={true}
            showCommonExtensions={true}
            persistAuthorization={true}
          />
        </div>
      );

  return (
    <div className={`swagger-page-scope ${classes.pageContainer}`}>
      <style>{SWAGGER_UI_FIX_STYLES}</style>
      <div className={classes.toolbar}>
        <SwaggerSpecTabs
          className={classes.tabs}
          specMode={specMode}
          onSpecModeChange={setSpecMode}
        />
      </div>
      {content}
    </div>
  );
}

export default SwaggerPage;